import React, { useMemo, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, Send, User, MessageSquare, CalendarPlus } from 'lucide-react';
import { useLanguage } from './LanguageContext';
interface Barber {
  name: string;
  alias: string | null;
  phone: string;
  phoneRaw: string;
  image: string;
}
interface BookingModalProps {
  barber: Barber | null;
  isOpen: boolean;
  onClose: () => void;
}
function getBusinessHours(date: Date): {
  start: number;
  end: number;
} | null {
  const day = date.getDay();
  switch (day) {
    case 0:
      return {
        start: 10,
        end: 17
      };
    case 5:
      return {
        start: 8.5,
        end: 21
      };
    case 6:
      return {
        start: 8.5,
        end: 21
      };
    default:
      return {
        start: 8.5,
        end: 20
      };
  }
}
function generateTimeSlots(date: Date): string[] {
  const hours = getBusinessHours(date);
  if (!hours) return [];
  const slots: string[] = [];
  let current = hours.start;
  while (current < hours.end) {
    const h = Math.floor(current);
    const m = current % 1 === 0.5 ? 30 : 0;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
    slots.push(`${displayH}:${m.toString().padStart(2, '0')} ${ampm}`);
    current += 0.5;
  }
  return slots;
}
function formatDateLabel(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const daysEs = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const daysEn = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthsEs = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const monthsEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const days = lang === 'en' ? daysEn : daysEs;
  const months = lang === 'en' ? monthsEn : monthsEs;
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}
function generateCalendarEvent(barber: {
  name: string;
  alias: string | null;
  phone: string;
  phoneRaw: string;
}, date: string, time: string, clientName: string, lang: string) {
  // Parse date and time
  const [year, month, day] = date.split('-').map(Number);
  const timeMatch = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!timeMatch) return;
  let hours = parseInt(timeMatch[1]);
  const minutes = parseInt(timeMatch[2]);
  const ampm = timeMatch[3].toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  const startDate = new Date(year, month - 1, day, hours, minutes);
  const endDate = new Date(startDate.getTime() + 45 * 60 * 1000); // 45 min appointment
  function toICSDate(d: Date): string {
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }
  const barberDisplay = barber.alias ? `${barber.name} (${barber.alias})` : barber.name;
  const title = lang === 'en' ? `Haircut with ${barberDisplay} - VillaConBarberShop` : `Corte con ${barberDisplay} - VillaConBarberShop`;
  const description = lang === 'en' ? `Appointment with ${barberDisplay}\\nPhone: ${barber.phone}\\nClient: ${clientName}\\n\\nVillaConBarberShop\\n926A East Tremont Ave, Bronx, NY 10460` : `Cita con ${barberDisplay}\\nTeléfono: ${barber.phone}\\nCliente: ${clientName}\\n\\nVillaConBarberShop\\n926A East Tremont Ave, Bronx, NY 10460`;
  const icsContent = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//VillaConBarberShop//Booking//EN', 'BEGIN:VEVENT', `DTSTART:${toICSDate(startDate)}`, `DTEND:${toICSDate(endDate)}`, `SUMMARY:${title}`, `DESCRIPTION:${description}`, 'LOCATION:926A East Tremont Ave\\, Bronx\\, NY 10460', 'BEGIN:VALARM', 'TRIGGER:-PT30M', 'ACTION:DISPLAY', `DESCRIPTION:${lang === 'en' ? 'Haircut appointment in 30 minutes' : 'Cita de corte en 30 minutos'}`, 'END:VALARM', 'BEGIN:VALARM', 'TRIGGER:-PT120M', 'ACTION:DISPLAY', `DESCRIPTION:${lang === 'en' ? 'Haircut appointment in 2 hours' : 'Cita de corte en 2 horas'}`, 'END:VALARM', 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
  const blob = new Blob([icsContent], {
    type: 'text/calendar;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `VillaCon_${barber.name.replace(/\s+/g, '_')}_${date}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
export function BookingModal({
  barber,
  isOpen,
  onClose
}: BookingModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const {
    t,
    lang
  } = useLanguage();
  const today = new Date();
  const minDate = today.toISOString().split('T')[0];
  const maxDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const timeSlots = useMemo(() => {
    if (!selectedDate) return [];
    return generateTimeSlots(new Date(selectedDate + 'T12:00:00'));
  }, [selectedDate]);
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!barber || !firstName || !lastName || !selectedDate || !selectedTime) return;
    const barberDisplay = barber.alias ? `${barber.name} (${barber.alias})` : barber.name;
    const dateLabel = formatDateLabel(selectedDate, lang);
    const smsBody = t('booking.smsTemplate', {
      barber: barberDisplay,
      first: firstName,
      last: lastName,
      date: dateLabel,
      time: selectedTime
    }) + (message ? ` ${message}` : '');
    window.open(`whatsapp://send?phone=1${barber.phoneRaw}&text=${encodeURIComponent(smsBody)}`, '_self');
    // Generate calendar event with reminder
    generateCalendarEvent(barber, selectedDate, selectedTime, `${firstName} ${lastName}`, lang);
    setSubmitted(true);
  }
  function handleClose() {
    setFirstName('');
    setLastName('');
    setSelectedDate('');
    setSelectedTime('');
    setMessage('');
    setSubmitted(false);
    onClose();
  }
  if (!barber) return null;
  const displayName = barber.alias || barber.name;
  return <AnimatePresence data-id="element-82">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={handleClose} data-id="element-83">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="bg-barber-gray border border-barber-lightGray rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-84">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-barber-lightGray" data-id="element-85">
              <div className="flex items-center gap-3" data-id="element-86">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-barber-gold/30 shrink-0" data-id="element-87">
                  <img src={barber.image} alt={barber.name} className="w-full h-full object-cover" data-id="element-88" />
                </div>
                <div data-id="element-89">
                  <h3 className="font-heading text-xl font-bold text-barber-text uppercase" data-id="element-90">
                    {barber.name}
                  </h3>
                  {barber.alias && <p className="text-barber-gold text-sm font-heading tracking-widest" data-id="element-91">
                      "{barber.alias}"
                    </p>}
                </div>
              </div>
              <button onClick={handleClose} className="text-barber-muted hover:text-barber-text p-1" data-id="element-92">
                <X size={24} data-id="element-93" />
              </button>
            </div>

            {!submitted ? <form onSubmit={handleSubmit} className="p-5 space-y-5" data-id="element-94">
                <p className="text-barber-muted text-sm" data-id="element-95">
                  {t('booking.subtitle', {
              name: displayName
            })}
                </p>

                <div className="grid grid-cols-2 gap-3" data-id="element-96">
                  <div data-id="element-97">
                    <label className="flex items-center gap-2 text-sm text-barber-muted mb-2 font-heading uppercase tracking-wider" data-id="element-98">
                      <User size={14} className="text-barber-gold" data-id="element-99" />{' '}
                      {t('booking.name')}
                    </label>
                    <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t('booking.namePh')} className="w-full bg-barber-black border border-barber-lightGray rounded-sm px-4 py-3 text-barber-text placeholder:text-barber-muted/50 focus:outline-none focus:border-barber-gold transition-colors" data-id="element-100" />
                  </div>
                  <div data-id="element-101">
                    <label className="flex items-center gap-2 text-sm text-barber-muted mb-2 font-heading uppercase tracking-wider" data-id="element-102">
                      {t('booking.lastName')}
                    </label>
                    <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t('booking.lastNamePh')} className="w-full bg-barber-black border border-barber-lightGray rounded-sm px-4 py-3 text-barber-text placeholder:text-barber-muted/50 focus:outline-none focus:border-barber-gold transition-colors" data-id="element-103" />
                  </div>
                </div>

                <div data-id="element-104">
                  <label className="flex items-center gap-2 text-sm text-barber-muted mb-2 font-heading uppercase tracking-wider" data-id="element-105">
                    <Calendar size={14} className="text-barber-gold" data-id="element-106" />{' '}
                    {t('booking.date')}
                  </label>
                  <input type="date" required value={selectedDate} onChange={e => {
              setSelectedDate(e.target.value);
              setSelectedTime('');
            }} min={minDate} max={maxDate} className="w-full bg-barber-black border border-barber-lightGray rounded-sm px-4 py-3 text-barber-text focus:outline-none focus:border-barber-gold transition-colors [color-scheme:dark]" data-id="element-107" />
                  {selectedDate && <p className="text-barber-gold text-sm mt-1" data-id="element-108">
                      {formatDateLabel(selectedDate, lang)}
                    </p>}
                </div>

                {selectedDate && timeSlots.length > 0 && <div data-id="element-109">
                    <label className="flex items-center gap-2 text-sm text-barber-muted mb-2 font-heading uppercase tracking-wider" data-id="element-110">
                      <Clock size={14} className="text-barber-gold" data-id="element-111" />{' '}
                      {t('booking.time')}
                    </label>
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1" data-id="element-112">
                      {timeSlots.map(slot => <button key={slot} type="button" onClick={() => setSelectedTime(slot)} className={`px-3 py-2 rounded-sm text-sm font-heading tracking-wider transition-colors ${selectedTime === slot ? 'bg-barber-gold text-barber-black font-bold' : 'bg-barber-black border border-barber-lightGray text-barber-muted hover:border-barber-gold/50 hover:text-barber-text'}`} data-id="element-113">
                          {slot}
                        </button>)}
                    </div>
                  </div>}

                <div data-id="element-114">
                  <label className="flex items-center gap-2 text-sm text-barber-muted mb-2 font-heading uppercase tracking-wider" data-id="element-115">
                    <MessageSquare size={14} className="text-barber-gold" data-id="element-116" />{' '}
                    {t('booking.msg')}
                  </label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={t('booking.msgPh')} rows={3} className="w-full bg-barber-black border border-barber-lightGray rounded-sm px-4 py-3 text-barber-text placeholder:text-barber-muted/50 focus:outline-none focus:border-barber-gold transition-colors resize-none" data-id="element-117" />
                </div>

                <button type="submit" disabled={!firstName || !lastName || !selectedDate || !selectedTime} className="w-full bg-barber-red hover:bg-barber-redHover disabled:bg-barber-lightGray disabled:text-barber-muted text-white font-heading font-bold text-lg tracking-wider py-4 rounded-sm transition-colors flex items-center justify-center gap-3" data-id="element-118">
                  <Send size={20} data-id="element-119" />
                  {t('booking.submit')}
                </button>

                <p className="text-barber-muted/60 text-xs text-center flex items-center justify-center gap-1.5" data-id="element-120">
                  <CalendarPlus size={12} data-id="element-121" />
                  {lang === 'en' ? 'A calendar reminder will be added automatically' : 'Se agregará un recordatorio al calendario automáticamente'}
                </p>
              </form> : <div className="p-8 text-center space-y-4" data-id="element-122">
                <div className="w-16 h-16 bg-barber-gold/20 rounded-full flex items-center justify-center mx-auto" data-id="element-123">
                  <CalendarPlus className="text-barber-gold w-8 h-8" data-id="element-124" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-barber-text uppercase" data-id="element-125">
                  {t('booking.success')}
                </h3>
                <p className="text-barber-muted" data-id="element-126">
                  {t('booking.successSub', {
              name: displayName
            })}
                </p>
                <p className="text-barber-gold text-sm flex items-center justify-center gap-2" data-id="element-127">
                  <Calendar size={16} data-id="element-128" />
                  {lang === 'en' ? 'Calendar reminder added!' : '¡Recordatorio de calendario agregado!'}
                </p>
                <button onClick={handleClose} className="mt-4 bg-barber-gold hover:bg-barber-goldHover text-barber-black font-heading font-bold tracking-wider px-8 py-3 rounded-sm transition-colors" data-id="element-129">
                  {t('booking.close')}
                </button>
              </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}